/**
 * CreateSkillPage
 * Page for creating and editing skills
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Upload, Maximize2 } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { Button, Input, Textarea, Dropdown, Toggle, FormSection } from '../../components/ui';
import { ExpandedEditorModal } from '../../components/skills/ExpandedEditorModal';
import { skillsApi } from '../../services/api';
import type { CreateSkillRequest, UpdateSkillRequest, Skill } from '../../types';
import styles from './CreateSkillPage.module.css';

// Available skill categories
const SKILL_CATEGORIES = [
  'Business Analysis',
  'Code Generation',
  'Code Review',
  'Data Analysis',
  'DevOps',
  'Documentation',
  'Project Management',
  'Quality Assurance',
  'Security',
  'Testing',
  'Architecture',
  'Design',
  'Research',
  'Custom',
];

const DEFAULT_INSTRUCTIONS_TEMPLATE = `## Purpose
Describe what this skill helps the assistant accomplish.

## Context
When should this skill be used? What scenarios does it address?

## Instructions
Provide detailed step-by-step instructions for the assistant:
`;

interface SkillFormData {
  projectId: string;
  sharedWithProject: boolean;
  name: string;
  description: string;
  categories: string[];
  instructions: string;
}

export const CreateSkillPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<SkillFormData>({
    projectId: 'current-project',
    sharedWithProject: false,
    name: '',
    description: '',
    categories: [],
    instructions: DEFAULT_INSTRUCTIONS_TEMPLATE,
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpandedEditorOpen, setIsExpandedEditorOpen] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Load existing skill for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadSkill(id);
    }
  }, [isEditMode, id]);

  const loadSkill = async (skillId: string) => {
    try {
      const response = await skillsApi.getById(skillId);
      if (response.success && response.data) {
        const skill: Skill = response.data;
        setFormData({
          projectId: skill.projectId || 'current-project',
          sharedWithProject: skill.sharedWithProject,
          name: skill.name,
          description: skill.description,
          categories: skill.categories,
          instructions: skill.instructions,
        });
      }
    } catch (err) {
      console.error('Failed to load skill:', err);
      setError('Failed to load skill. Please try again.');
    }
  };

  // Validation
  const validateKebabCase = (name: string): boolean => {
    const kebabCaseRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    return kebabCaseRegex.test(name);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, name: value });

    if (value && !validateKebabCase(value)) {
      setNameError('Name must be in kebab-case (lowercase, numbers, hyphens only)');
    } else {
      setNameError(null);
    }
  };

  const handleDownloadExample = () => {
    const exampleContent = `# Example Skill Template

${DEFAULT_INSTRUCTIONS_TEMPLATE}

## Example Use Cases
- Use case 1
- Use case 2
- Use case 3

## Best Practices
- Best practice 1
- Best practice 2
`;

    const blob = new Blob([exampleContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'example-skill-template.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportFromFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,.txt,.json';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          const text = await file.text();
          setFormData({ ...formData, instructions: text });
        } catch (err) {
          console.error('Failed to read file:', err);
          setError('Failed to import file. Please try again.');
        }
      }
    };
    input.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateKebabCase(formData.name)) {
      setNameError('Name must be in kebab-case (lowercase, numbers, hyphens only)');
      return;
    }

    if (formData.description.length > 1000) {
      setError('Description must be 1000 characters or less');
      return;
    }

    if (formData.instructions.length > 30000) {
      setError('Instructions must be 30,000 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode && id) {
        const updateData: UpdateSkillRequest = {
          name: formData.name,
          description: formData.description,
          instructions: formData.instructions,
          categories: formData.categories,
          sharedWithProject: formData.sharedWithProject,
        };
        await skillsApi.update(id, updateData);
      } else {
        const createData: CreateSkillRequest = {
          name: formData.name,
          description: formData.description,
          instructions: formData.instructions,
          categories: formData.categories,
          projectId: formData.projectId,
          sharedWithProject: formData.sharedWithProject,
        };
        await skillsApi.create(createData);
      }
      navigate('/skills');
    } catch (err) {
      console.error('Failed to save skill:', err);
      setError('Failed to save skill. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/skills');
  };

  return (
    <AppLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backButton}
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className={styles.title}>
              {isEditMode ? 'Edit Skill' : 'Create New Skill'}
            </h1>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.linkButton}
              onClick={handleDownloadExample}
              type="button"
            >
              <Download size={16} />
              Download Example
            </button>
            <button
              className={styles.linkButton}
              onClick={handleImportFromFile}
              type="button"
            >
              <Upload size={16} />
              Import from File
            </button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !!nameError}
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Skill' : '+ Create Skill'}
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Section 1: Skill Setup */}
          <FormSection title="Skill Setup">
            <div className={styles.formGrid}>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <label className={styles.label}>
                    Project name <span className={styles.required}>*</span>
                  </label>
                  <Dropdown
                    options={[
                      { value: 'current-project', label: 'Current Project (user@example.com)' },
                    ]}
                    value={formData.projectId}
                    onChange={(value) => setFormData({ ...formData, projectId: value })}
                    fullWidth
                  />
                </div>
                <div className={styles.toggleField}>
                  <label className={styles.label}>
                    Shared with project
                  </label>
                  <Toggle
                    checked={formData.sharedWithProject}
                    onChange={(checked) =>
                      setFormData({ ...formData, sharedWithProject: checked })
                    }
                  />
                </div>
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>
                  Name <span className={styles.required}>*</span>
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="my-skill-name"
                  required
                  fullWidth
                />
                {nameError ? (
                  <p className={styles.errorHint}>{nameError}</p>
                ) : (
                  <p className={styles.hint}>Use kebab-case: lowercase, numbers, and hyphens only</p>
                )}
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>
                  Description <span className={styles.required}>*</span>
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of when to use this skill..."
                  rows={3}
                  maxLength={1000}
                  required
                  fullWidth
                />
                <p className={styles.charCount}>
                  ({formData.description.length}/1000)
                </p>
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>
                  Categories
                </label>
                <select
                  multiple
                  value={formData.categories}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                    if (selected.length <= 3) {
                      setFormData({ ...formData, categories: selected });
                    }
                  }}
                  className={styles.multiSelect}
                >
                  {SKILL_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <p className={styles.hint}>Choose up to 3 categories that describe your skill</p>
              </div>
            </div>
          </FormSection>

          {/* Section 2: Instructions */}
          <FormSection
            title="Instructions"
            subtitle="Define the skill content that will be provided to assistants."
          >
            <div className={styles.formField}>
              <div className={styles.instructionsHeader}>
                <label className={styles.label}>
                  Instructions ({formData.instructions.length}/30,000)
                </label>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setIsExpandedEditorOpen(true)}
                >
                  <Maximize2 size={16} />
                  Expand
                </button>
              </div>
              <Textarea
                value={formData.instructions}
                onChange={(e) =>
                  setFormData({ ...formData, instructions: e.target.value })
                }
                placeholder={DEFAULT_INSTRUCTIONS_TEMPLATE}
                rows={12}
                maxLength={30000}
                fullWidth
                className={styles.instructionsTextarea}
              />
            </div>
          </FormSection>
        </form>
      </div>

      {/* Expanded Editor Modal */}
      <ExpandedEditorModal
        isOpen={isExpandedEditorOpen}
        value={formData.instructions}
        onChange={(value) => setFormData({ ...formData, instructions: value })}
        onClose={() => setIsExpandedEditorOpen(false)}
        maxLength={30000}
      />
    </AppLayout>
  );
};

export default CreateSkillPage;
